"use client";

import { useEffect, useRef } from "react";
import { toast } from "sonner";

/**
 * Componente que captura erros globais do JavaScript (fora do React)
 * e mostra um toast pedindo reload quando detecta erro de Server Action.
 * 
 * NÃO faz auto-reload para evitar loops infinitos em ambientes instáveis.
 */
export function GlobalErrorHandler() {
  const hasShownToast = useRef(false);

  useEffect(() => {
    const showReloadToast = () => {
      if (hasShownToast.current) return;
      hasShownToast.current = true;

      toast.error("Sessão expirada", {
        description: "O sistema foi atualizado. Recarregue a página para continuar.",
        duration: Infinity,
        action: {
          label: "Recarregar",
          onClick: () => window.location.reload(),
        },
      });
    };

    const handleError = (event: ErrorEvent) => {
      const errorMessage = event.message || "";

      // Detecta erro de Server Action (específico, não genérico)
      if (
        errorMessage.includes("Failed to find Server Action") ||
        errorMessage.includes("older or newer deployment")
      ) {
        console.warn("[GlobalErrorHandler] Erro de Server Action detectado.");
        event.preventDefault();
        showReloadToast();
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const errorMessage =
        reason?.message || reason?.toString() || String(reason);

      // Detecta erro de Server Action em promises rejeitadas (específico)
      if (
        errorMessage.includes("Failed to find Server Action") ||
        errorMessage.includes("older or newer deployment")
      ) {
        console.warn("[GlobalErrorHandler] Erro de Server Action em Promise detectado.");
        event.preventDefault();
        showReloadToast();
      }
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  return null;
}

export default GlobalErrorHandler;
